import type { BlockContent, BlockKind } from '@/blocks/domain/types';
import { defaultCustomContent } from './customSections';
import { defaultCustomerContent } from './customerDefaults';
import { defaultFieldsFor } from './defaultFieldsFor';
import { defaultSignatureContent } from './signatureDefaults';

/**
 * Per-kind initialiser for the block editor's draft state.
 *
 * The editor needs a fully-shaped `BlockContent` BEFORE the version round-trip
 * completes so the right ContentEditor variant mounts immediately on every
 * navigation. The fallback used to be a hard-coded header dummy, which made the
 * editor flash header fields for every block kind. Centralising the defaults
 * here keeps the dispatcher honest and lets new kinds get their own initial
 * shape without touching the page component.
 */
export function defaultContentForKind(kind: BlockKind, documentTypeKey?: string | null): BlockContent {
  switch (kind) {
    case 'header':
      return { kind: 'header', fields: defaultFieldsFor('header', documentTypeKey) };
    case 'customer_block':
      return defaultCustomerContent(documentTypeKey);
    case 'scope_of_work':
      return { kind: 'scope_of_work', title: 'Scope of Work', bodyHtml: '' };
    case 'comments':
      return { kind: 'comments', title: 'Comments', bodyHtml: '' };
    case 'terms_conditions':
      return { kind: 'terms_conditions', title: 'Terms & Conditions', bodyHtml: '' };
    case 'custom_text':
      return { kind: 'custom_text', title: '', bodyHtml: '' };
    case 'custom':
      return defaultCustomContent();
    case 'scope_fields':
      return { kind: 'scope_fields', title: 'Project Details' };
    case 'line_items_table':
      return {
        kind: 'line_items_table',
        title: 'Line Items',
        columns: [
          { key: 'name', label: 'Item', visible: true, order: 0 },
          { key: 'description', label: 'Description', visible: true, order: 1 },
          { key: 'quantity', label: 'Qty', visible: true, order: 2 },
          { key: 'unit', label: 'Unit', visible: true, order: 3 },
          { key: 'rate', label: 'Rate', visible: true, order: 4 },
          { key: 'cost', label: 'Cost', visible: true, order: 5 },
          { key: 'margin', label: 'Margin %', visible: true, order: 6 },
          { key: 'total', label: 'Total', visible: true, order: 7 }
        ],
        showRateScheduleNote: false
      };
    case 'total':
      return {
        kind: 'total',
        showSubtotal: true,
        showAdjustments: true,
        grandTotalLabel: 'Estimated Total'
      };
    case 'roles_responsibilities':
      return {
        kind: 'roles_responsibilities',
        title: 'Roles & Responsibilities',
        rows: [],
        columns: {
          role: { visible: true, label: 'Role' },
          responsibility: { visible: true, label: 'Responsibility' }
        }
      };
    case 'signature':
      return defaultSignatureContent(documentTypeKey);
    case 'price_book':
      return {
        kind: 'price_book',
        title: 'Rate Schedule',
        serviceItemIds: [],
        columns: [
          { key: 'name', label: 'Service', visible: true, order: 0 },
          { key: 'description', label: 'Description', visible: false, order: 1 },
          { key: 'unit', label: 'Unit', visible: true, order: 2 },
          { key: 'rate', label: 'Regular', visible: true, order: 3 },
          { key: 'ot_rate', label: 'Overtime 1', visible: true, order: 4 },
          { key: 'ot_rate_2', label: 'Overtime 2', visible: true, order: 5 },
          { key: 'volume_rate', label: 'Yardage', visible: true, order: 6 },
          { key: 'min_charge', label: 'Min Charge', visible: false, order: 7 }
        ]
      };
    case 'spacer':
      return { kind: 'spacer', heightPx: 16 };
    case 'cover_page':
      return {
        kind: 'cover_page',
        title: 'Cover Page',
        showLogo: true,
        showCustomerName: true,
        showDate: true
      };
    default: {
      // Exhaustive check — adding a new BlockKind that lacks a default here is
      // a compile-time error.
      const _exhaustive: never = kind;
      throw new Error(`defaultContentForKind: missing branch for ${String(_exhaustive)}`);
    }
  }
}
